import {
  Typography,
  Box,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Card,
  Button,
  makeStyles,
} from '@material-ui/core';
import ImageIcon from '@material-ui/icons/Image';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import LaunchOutlinedIcon from '@material-ui/icons/LaunchOutlined';
import MovieIcon from '@material-ui/icons/Movie';

import ReadMoreParsedText from 'components/ReadMoreParsedText';
import { TiposDeCabecalhoMensagemInterativaEnum } from 'store/reducers/mensagens';
import parseTextToHtml from 'utils/parseTextToHtml';

interface PreviewMensagemCtaUrlProps {
  type?: TiposDeCabecalhoMensagemInterativaEnum | null;
  header?: string;
  body?: {
    text: string;
    caption: string;
  };
  buttonAction?: {
    label: string;
    url: string;
  };
}

const useStyles = makeStyles(theme => ({
  titulo: {
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    fontWeight: 600,
  },
  skeletonImage: {
    width: '100%',
    height: theme.spacing(15),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  icon: {
    color: theme.palette.primary.main,
  },
  cardContainer: {
    width: 300,
    height: 'fit-content',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1),
    gap: theme.spacing(1),
    borderRadius: theme.spacing(0.5),
  },
  sendButton: {
    borderTop: `1px solid ${theme.palette.divider}`,
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    '&:disabled': {
      color: theme.palette.primary.main,
    },
  },
  listItemText: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    wordBreak: 'break-word',
    whiteSpace: 'normal',
  },
}));

export function PreviewMensagemCtaUrl({
  type,
  header,
  body,
  buttonAction,
}: PreviewMensagemCtaUrlProps = {}) {
  const classes = useStyles();

  const renderHeader = () => {
    switch (type) {
      case TiposDeCabecalhoMensagemInterativaEnum.TEXTO:
        return (
          <Typography
            variant="subtitle2"
            className={classes.titulo}
            dangerouslySetInnerHTML={{
              __html: parseTextToHtml(header || 'Cabeçalho da mensagem'),
            }}
          />
        );
      case TiposDeCabecalhoMensagemInterativaEnum.IMAGEM:
        return (
          <Box className={classes.skeletonImage}>
            <ImageIcon className={classes.icon} />
          </Box>
        );
      case TiposDeCabecalhoMensagemInterativaEnum.DOCUMENTO:
        return (
          <ListItem disableGutters>
            <ListItemAvatar>
              <InsertDriveFileIcon fontSize="large" className={classes.icon} />
            </ListItemAvatar>
            <ListItemText primary="Seu documento" />
          </ListItem>
        );
      case TiposDeCabecalhoMensagemInterativaEnum.VIDEO:
        return (
          <Box className={classes.skeletonImage}>
            <MovieIcon className={classes.icon} />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Card elevation={0} className={classes.cardContainer}>
      {renderHeader()}
      <ListItemText
        className={classes.listItemText}
        primary={
          <ReadMoreParsedText
            texto={body?.text || 'Mensagem'}
            variant="body2"
          />
        }
        secondary={
          <Typography
            variant="caption"
            dangerouslySetInnerHTML={{
              __html: parseTextToHtml(body?.caption || 'Rodapé da mensagem'),
            }}
          />
        }
      />
      <Button
        className={classes.sendButton}
        variant="text"
        startIcon={<LaunchOutlinedIcon />}
        size="small"
        disabled
        fullWidth
      >
        {buttonAction?.label || 'CTA URL'}
      </Button>
    </Card>
  );
}
